import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { BsClipboard, BsBoxArrowUpRight } from 'react-icons/bs';

import { setCreditCardOverride } from '../../../services/requests/orders/orders-requests';
import { selectLoggedInUserInfo } from '../../../store/slices/auth-slice';
import { OrderType } from './orders-types';
import { usePermissions } from '../../hooks/usePermissions';
import { ordersActionPermissions } from './orders-datasource';

/**
 * Legacy `lblClosureNote` — shown when the order is Closed (billed) and locked
 * down. Non-site-administrators can only edit the Logistics Only Notes; the
 * backend enforces this on save.
 */
export const OrderClosureNote: React.FC<any> = ({ entity }) => {
  if (!entity?.inOrderClosureLockdown) return null;
  return (
    <div className="alert alert-warning fw-bold fst-italic mb-2 py-2">
      Order is Closed - only Logistics Only Notes can be updated
    </div>
  );
};

/**
 * Legacy `lblEditDewarShipmentPreWarning` — shown on the Products Delivered grid
 * when a JDE-uploaded invoice already exists for this Dewar order; edits here
 * will not affect billing.
 */
export const OrderDewarInvoiceUploadedNote: React.FC<any> = ({ entity }) => {
  if (!entity?.dewarInvoiceUploaded) return null;
  return (
    <div className="alert alert-warning fw-bold fst-italic mb-2 py-2">
      An invoice for this order has already been uploaded. Changes here will not affect billing.
    </div>
  );
};


/**
 * Legacy clipboard button beside the Customer dropdown (`ClipBoardButton` /
 * `CopyToClipboard()`). Copies the selected customer name to the clipboard.
 */
export const OrderCustomerClipboard: React.FC<any> = ({ watch }) => {
  const [copied, setCopied] = useState(false);
  const customerName = watch?.('customerName');

  const copy = async () => {
    const text = String(customerName ?? '');
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard API unavailable (non-secure context) — ignore silently.
    }
  };

  return (
    <div className="mb-1 d-flex align-items-end" style={{ height: '100%' }}>
      <Button
        variant="outline-secondary"
        size="sm"
        onClick={copy}
        disabled={!customerName}
        title={copied ? 'Copied!' : 'Copy customer name'}
        aria-label={copied ? 'Copied!' : 'Copy customer name'}
      >
        <BsClipboard />
      </Button>
    </div>
  );
};

/**
 * Legacy `btnParentOrderLink` — opens the parent order's edit page. The backend
 * resolves the parent order number to its internal id (`parentOrderId`); the
 * link is only shown when a parent exists.
 */
export const OrderParentLink: React.FC<any> = ({ entity, watch }) => {
  const navigate = useNavigate();
  const parentOrder = watch?.('parentOrder');
  const parentOrderId = entity?.parentOrderId;

  if (!parentOrder || !parentOrderId) return null;

  return (
    <div className="mb-1 d-flex align-items-end" style={{ height: '100%' }}>
      <Button
        variant="outline-primary"
        size="sm"
        onClick={() => navigate(`/orders/edit/${parentOrderId}`)}
        title={`Open parent order ${parentOrder}`}
        aria-label={`Open parent order ${parentOrder}`}
      >
        <BsBoxArrowUpRight />
      </Button>
    </div>
  );
};

/**
 * Legacy Credit Card Override control (`btnCreditCardOverride` /
 * `DetermineCreditCardOverrideVisibility`). Dewar-only, and only shown when
 * Credit Card Payment is selected. If the order was already overridden, a
 * confirmation label is shown; otherwise an Override button raises the flag via
 * the backend (the external Credit Card Processing Application then charges the
 * card).
 */
export const OrderCreditCardOverride: React.FC<any> = ({ entity, watch, setValue }) => {
  const [actionAccess] = usePermissions(ordersActionPermissions);
  const orderTypeId = Number(watch?.('orderTypeId'));
  const creditCardPayment = !!watch?.('creditCardPayment');
  const overridden = !!watch?.('creditCardOverride');
  const alreadyCharged = !!entity?.creditCardAlreadyCharged;
  const orderId = entity?.id;
  const [saving, setSaving] = useState(false);

  const isDewar = orderTypeId === OrderType.Dewar;
  if (!isDewar || !creditCardPayment) return null;

  const label = <div className="form-label fw-bold mb-1">Credit Card Override</div>;

  const doOverride = async () => {
    if (!orderId) return;
    setSaving(true);
    try {
      await setCreditCardOverride(orderId);
      setValue?.('creditCardOverride', true);
    } finally {
      setSaving(false);
    }
  };

  // Legacy DetermineCreditCardOverrideVisibility(): once the order has been
  // overridden, or a card pre-authorization already exists, show a label
  // instead of the button.
  if (overridden || alreadyCharged) {
    return (
      <div className="mb-2">
        {label}
        <div className="fst-italic">
          {alreadyCharged
            ? 'Payment has already been made via credit card.'
            : 'Payment for Order has been manually overriden to be charged via credit card'}
        </div>
      </div>
    );
  }

  return (
    <div className="mb-2">
      {label}
      <Button
        variant="warning"
        size="sm"
        onClick={doOverride}
        disabled={saving || !orderId || !actionAccess}
        title={
          'Clicking this button will charge the credit card on file the total amount of the invoice ' +
          'regardless if the order was already closed out in JDE. Use this with extreme caution as you ' +
          'can double charge the customer (Via JDE and via credit card).'
        }
      >
        {saving && <Spinner animation="border" size="sm" className="me-2" />}
        Override
      </Button>
    </div>
  );
};

/**
 * Legacy `btnPrintInternationalProForma`: opens the SSRS
 * International_Dewar_Billing_Invoice_ProForma report for the order in a new tab.
 * Passes `ReportUser` = the logged-in user's display name (legacy
 * `Membership.GetUser(GlobalBll.getUserID()).Comment`), matching legacy exactly.
 */
export const OrderPrintProForma: React.FC<any> = ({ watch }) => {
  const userInfo: any = useSelector(selectLoggedInUserInfo);
  const orderNumber = watch?.('orderNumber');
  // Legacy btnPrintFinalInternationalInvoice: visible only when DateFulfilled is set and status is "Closed".
  const showFinalInvoice = !!watch?.('dateFulfilled') && watch?.('statusText') === 'Closed';

  const print = (report: string) => {
    const reportUser = userInfo?.displayName ?? '';
    const root = `http://${process.env.REACT_APP_RPT}/ReportServer`;
    window.open(
      `${root}/Pages/ReportViewer.aspx?%2fBulk+Helium+Reports%2f${report}&rs:Command=Render&orderid=${orderNumber ?? 0}&ReportUser=${reportUser}`,
      '_blank',
    );
  };

  return (
    <div className="mb-2 d-flex gap-2">
      <Button variant="secondary" size="sm" onClick={() => print('International_Dewar_Billing_Invoice_ProForma')}>
        Print International Pro Forma
      </Button>
      {showFinalInvoice && (
        <Button variant="secondary" size="sm" onClick={() => print('International_Dewar_Billing_Invoice')}>
          Print International Final Invoice
        </Button>
      )}
    </div>
  );
};
